import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RefreshCw, Timer, Volume2, VolumeX, ChevronLeft } from 'lucide-react';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { soundThemes } from '../config/soundContent';
import { Capacitor } from '@capacitor/core';
import { NativeAudio } from '@capacitor-community/native-audio';
import { NowPlaying } from 'capacitor-now-playing';

const AudioPlayer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useAppSettings();

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showGoodNight, setShowGoodNight] = useState(false);
  const [volume, setVolume] = useState(55); // Default volume
  const [isMuted, setIsMuted] = useState(false);
  const [timerDuration, setTimerDuration] = useState<number | null>(null);
  const [showTimerSelector, setShowTimerSelector] = useState(false);
  const [isLooping, setIsLooping] = useState(false); // Default looping state
  const [error, setError] = useState<string>('');
  const [actualDuration, setActualDuration] = useState<number>(0);

  const platform = Capacitor.getPlatform();
  const isNativePlatform = platform === 'ios' || platform === 'android';

  // Memos and Helper Functions
  const { theme, contentId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const content = params.get('content');
    const type = params.get('type');
    const themeObj = (type === 'white_noise' || type === 'meditation')
      ? (soundThemes[content as keyof typeof soundThemes] || soundThemes.default)
      : soundThemes.default;
    return { theme: themeObj, contentId: content || 'default' };
  }, [location.search]);

  // Native audio asset path: iOS uses filename only (Bundle.main path(forResource:ofType:)); Android uses audios/ from cap sync
  const getNativeAssetPath = (audioSrc: string | null) => {
    if (!audioSrc) return '';
    const filename = audioSrc.split('/').pop() || '';
    return platform === 'ios' ? filename : `audios/${filename}`;
  };

  const nativeAssetLoadedRef = useRef<string | null>(null);
  const isSeekingRef = useRef(false);
  const isLoopingRef = useRef(isLooping);
  const timerDurationRef = useRef<number | null>(null);
  const timerStartTimeRef = useRef<number | null>(null);
  isLoopingRef.current = isLooping;

  // Logarithmic volume scaling for natural hearing perception
  // 0-100 slider value -> 0.0-1.0 audio volume
  const getEffectiveVolume = () => {
    if (isMuted) return 0;
    // Square the specific ratio to approximate logarithmic perception
    // e.g. 50% slider -> 0.25 amplitude -> ~ -12dB
    return Math.pow(volume / 100, 2);
  };

  const displayDuration = actualDuration > 0 ? actualDuration : theme.duration;

  // Effects
  useEffect(() => {
    let timerTimeout: NodeJS.Timeout;
    if (timerDuration && isPlaying) {
      timerTimeout = setTimeout(async () => {
        if (isNativePlatform && contentId !== 'default' && theme.audioSrc) {
          try {
            await NativeAudio.pause({ assetId: contentId });
          } catch {}
        }
        setIsPlaying(false);
        setShowGoodNight(true);
        setTimerDuration(null);
        timerDurationRef.current = null;
      }, timerDuration * 1000);
    }
    return () => clearTimeout(timerTimeout);
  }, [timerDuration, isPlaying, isNativePlatform, contentId, theme.audioSrc]);

  // Native audio (Capacitor): preload, play/loop, and keep playing when screen is locked
  useEffect(() => {
    if (!isNativePlatform || !theme.audioSrc || contentId === 'default') return;

    const assetId = contentId;
    const assetPath = getNativeAssetPath(theme.audioSrc);
    if (!assetPath) return;

    let progressInterval: ReturnType<typeof setInterval> | null = null;
    let completeListenerHandle: { remove: () => Promise<void> } | null = null;
    let mounted = true;

    (async () => {
      try {
        await NativeAudio.configure({ focus: true });
        await NativeAudio.preload({
          assetId,
          assetPath,
          audioChannelNum: 1,
          volume: getEffectiveVolume(),
          isUrl: false
        });
        if (!mounted) return;
        nativeAssetLoadedRef.current = assetId;
        const { duration } = await NativeAudio.getDuration({ assetId });
        if (Number.isFinite(duration)) setActualDuration(duration);
        else setActualDuration(theme.duration);
        setError('');

        if (isLooping) {
          await NativeAudio.loop({ assetId });
        } else {
          await NativeAudio.play({ assetId });
        }
        if (!mounted) return;
        setIsPlaying(true);

        // When plugin fires 'complete' (e.g. iOS numberOfLoops didn't stick), restart loop if we're in loop mode
        const handleComplete = (event: { assetId: string }) => {
          if (!mounted || event.assetId !== assetId) return;
          if (isLoopingRef.current) {
            NativeAudio.loop({ assetId }).catch(() => {});
          }
        };
        completeListenerHandle = await NativeAudio.addListener('complete', handleComplete);

        // Start progress polling only after asset is loaded and playing
        progressInterval = setInterval(async () => {
          if (!mounted || isSeekingRef.current) return;
          try {
            const { isPlaying: playing } = await NativeAudio.isPlaying({ assetId });
            if (!playing) return;
            const { currentTime } = await NativeAudio.getCurrentTime({ assetId });
            setElapsed(currentTime);
            
            // Check if timer has expired and pause playback
            if (timerDurationRef.current !== null && timerStartTimeRef.current !== null) {
              const elapsedSinceTimerSet = currentTime - timerStartTimeRef.current;
              if (elapsedSinceTimerSet >= timerDurationRef.current) {
                await NativeAudio.pause({ assetId });
                setIsPlaying(false);
                timerDurationRef.current = null;
                timerStartTimeRef.current = null;
                setTimerDuration(null); // Clear timer after it expires
              }
            }
          } catch {
            // ignore (e.g. asset already unloaded on unmount)
          }
        }, 500);
      } catch (e) {
        console.error('NativeAudio preload/play failed', e);
        setError('Failed to load audio');
      }
    })();

    return () => {
      mounted = false;
      nativeAssetLoadedRef.current = null;
      if (progressInterval) clearInterval(progressInterval);
      completeListenerHandle?.remove().catch(() => {});
      NativeAudio.stop({ assetId }).catch(() => {});
      NativeAudio.unload({ assetId }).catch(() => {});
      setIsPlaying(false);
    };
  }, [isNativePlatform, theme.audioSrc, theme.duration, contentId, isLooping]);

  // Sync native volume when volume/mute changes (only after asset is loaded to avoid "Asset is not loaded")
  useEffect(() => {
    if (!isNativePlatform || !theme.audioSrc || contentId === 'default') return;
    if (nativeAssetLoadedRef.current !== contentId) return;
    const vol = Math.max(0.1, getEffectiveVolume());
    NativeAudio.setVolume({ assetId: contentId, volume: vol }).catch(() => {});
  }, [isNativePlatform, contentId, volume, isMuted, getEffectiveVolume]);

  // iOS: MPNowPlayingInfoCenter (native lock screen bar). Web: Media Session API.
  useEffect(() => {
    if (!theme.audioSrc || contentId === 'default') return;

    const title = t(theme.title);
    const artist = 'Lumi';
    const artwork = theme.backgroundImage;
    const duration = displayDuration > 0 ? displayDuration : theme.duration;
    const position = elapsed;
    const playbackRate = isPlaying ? 1 : 0;

    if (isNativePlatform) {
      NowPlaying.setNowPlaying({
        title,
        artist,
        artwork,
        duration,
        position,
        playbackRate
      }).catch(() => {});
      return;
    }

    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const nav = navigator as Navigator & { mediaSession: MediaSession };
    nav.mediaSession.metadata = new MediaMetadata({
      title,
      artist,
      album: '',
      artwork: artwork ? [{ src: artwork, sizes: '512x512', type: 'image/jpeg' }] : undefined
    });
    nav.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    if (duration > 0) {
      try {
        nav.mediaSession.setPositionState({ duration, playbackRate: 1, position });
      } catch (_) {}
    }
  }, [isNativePlatform, theme.audioSrc, theme.title, theme.backgroundImage, theme.duration, contentId, t, displayDuration, elapsed, isPlaying]);

  // Lock screen play/pause/seek (iOS): MPRemoteCommandCenter fires events; we drive NativeAudio
  useEffect(() => {
    if (!isNativePlatform || contentId === 'default' || !theme.audioSrc) return;

    const onPlay = () => {
      (isLoopingRef.current ? NativeAudio.loop({ assetId: contentId }) : NativeAudio.play({ assetId: contentId }))
        .then(() => setIsPlaying(true))
        .catch(() => {});
    };
    const onPause = () => {
      NativeAudio.pause({ assetId: contentId }).then(() => setIsPlaying(false)).catch(() => {});
    };
    const onSeekTo = (event: { position: number }) => {
      const duration = actualDuration > 0 ? actualDuration : theme.duration;
      const t = Math.max(0, Math.min(duration, event.position));
      setElapsed(t);
      // Reset timer start time when seeking from lock screen, so timer counts from new position
      if (timerDurationRef.current !== null) {
        timerStartTimeRef.current = t;
      }
      NativeAudio.play({ assetId: contentId, time: t }).catch(() => {});
    };

    let playHandle: Promise<{ remove: () => Promise<void> }> | null = null;
    let pauseHandle: Promise<{ remove: () => Promise<void> }> | null = null;
    let seekHandle: Promise<{ remove: () => Promise<void> }> | null = null;
    playHandle = NowPlaying.addListener('play', onPlay);
    pauseHandle = NowPlaying.addListener('pause', onPause);
    seekHandle = NowPlaying.addListener('seekTo', onSeekTo);
    return () => {
      playHandle?.then((h) => h.remove()).catch(() => {});
      pauseHandle?.then((h) => h.remove()).catch(() => {});
      seekHandle?.then((h) => h.remove()).catch(() => {});
    };
  }, [isNativePlatform, contentId, theme.audioSrc, theme.duration, actualDuration]);

  // Media Session action handlers (web + fallback): play/pause/seek from lock screen or Control Center
  useEffect(() => {
    if (!theme.audioSrc || contentId === 'default') return;
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;

    const nav = navigator as Navigator & { mediaSession: MediaSession };
    nav.mediaSession.setActionHandler('play', () => {
      if (contentId === 'default') return;
      (isLoopingRef.current ? NativeAudio.loop({ assetId: contentId }) : NativeAudio.play({ assetId: contentId }))
        .then(() => setIsPlaying(true))
        .catch(() => {});
    });
    nav.mediaSession.setActionHandler('pause', () => {
      if (contentId === 'default') return;
      NativeAudio.pause({ assetId: contentId }).then(() => setIsPlaying(false)).catch(() => {});
    });
    const seekAmount = 15;
    nav.mediaSession.setActionHandler('seekbackward', () => {
      if (contentId === 'default') return;
      NativeAudio.getCurrentTime({ assetId: contentId }).then(({ currentTime }) => {
        const t = Math.max(0, currentTime - seekAmount);
        setElapsed(t);
        return NativeAudio.play({ assetId: contentId, time: t });
      }).catch(() => {});
    });
    nav.mediaSession.setActionHandler('seekforward', () => {
      if (contentId === 'default') return;
      NativeAudio.getCurrentTime({ assetId: contentId }).then(({ currentTime }) => {
        const end = actualDuration > 0 ? actualDuration : theme.duration;
        const t = Math.min(end, currentTime + seekAmount);
        setElapsed(t);
        return NativeAudio.play({ assetId: contentId, time: t });
      }).catch(() => {});
    });
    return () => {
      nav.mediaSession.setActionHandler('play', null);
      nav.mediaSession.setActionHandler('pause', null);
      nav.mediaSession.setActionHandler('seekbackward', null);
      nav.mediaSession.setActionHandler('seekforward', null);
    };
  }, [theme.audioSrc, theme.duration, contentId, actualDuration]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = displayDuration > 0 ? (elapsed / displayDuration) * 100 : 0;

  const handleTimerSet = async (minutes: number) => {
    const seconds = minutes * 60;
    setTimerDuration(seconds);
    timerDurationRef.current = seconds;
    
    // Record current playback time as timer start point
    if (isNativePlatform && contentId !== 'default' && theme.audioSrc) {
      try {
        const { currentTime } = await NativeAudio.getCurrentTime({ assetId: contentId });
        timerStartTimeRef.current = currentTime;
      } catch {
        timerStartTimeRef.current = elapsed;
      }
    } else {
      timerStartTimeRef.current = elapsed;
    }
    
    setShowTimerSelector(false);
  };

  const formatTimerDuration = (seconds: number | null): string => {
    if (seconds === null) return t('audioPlayer.timer');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handlePlayPause = async () => {
    if (!theme.audioSrc || contentId === 'default') return;
    try {
      if (isPlaying) {
        await NativeAudio.pause({ assetId: contentId });
        setIsPlaying(false);
      } else {
        if (isLooping) await NativeAudio.loop({ assetId: contentId });
        else await NativeAudio.play({ assetId: contentId });
        setIsPlaying(true);
      }
    } catch (e) {
      console.error('NativeAudio play/pause failed', e);
    }
  };



  if (!theme.audioSrc) {
    return (
      <div className="min-h-screen text-white relative overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-4">{t('audioPlayer.notFound')}</h1>
          <button
            onClick={() => navigate(createPageUrl("ContentLibrary"))}
            className="px-6 py-3 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
          >
            {t('audioPlayer.backToLibrary')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen text-white relative overflow-hidden" style={{ touchAction: 'none', position: 'fixed', top: 0, left: 0 }}>
      {/* Dynamic Background (z-10) */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center z-10"
        style={{ backgroundImage: `url(${theme.backgroundImage})` }}
        animate={{ scale: isPlaying ? 1.05 : 1 }}
        transition={{ duration: 30, ease: 'linear' }}
      />
      {/* Gradient Overlay (z-10) */}
      <div
        className="absolute inset-0 z-10"
        style={{ background: `linear-gradient(to bottom, ${theme.gradientFrom}99 0%, ${theme.gradientTo}E6 100%)` }}
      />

      <AnimatePresence>
        {showGoodNight && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/50"
            onClick={() => setShowGoodNight(false)}
          >
            <p className="text-4xl font-semibold">{t('audioPlayer.goodNight')}</p>
            <p className="text-sm opacity-70 mt-2">{t('audioPlayer.tapToDismiss')}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-20 flex flex-col h-screen p-4" style={{ maxHeight: '100vh', overflow: 'hidden' }}>
        {/* Header - 移除退出按钮 */}
        <header className="flex justify-between items-center pt-8 pb-[30vw]">
          <div />
        </header>

        {/* Top Section */}
        <div className="text-center mb-[10vw]">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-bold tracking-tight"
          >
            {t(theme.title)}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-base font-light opacity-70 mt-3"
          >
            {formatTime(elapsed)} / {formatTime(displayDuration)}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8, y: 0 }}
            transition={{ delay: 0.5, duration: 1.5 }}
            className="text-lg italic opacity-80 mt-3 font-serif"
          >
            "{t(theme.poeticSubtitle)}"
          </motion.p>
        </div>

        {/* Middle Section - Play Button & Progress */}
        <div className="flex-1 flex flex-col items-center justify-start">
          <div className="w-4/5 max-w-sm mb-10">
            <motion.div
              className="h-2 w-full bg-white/20 rounded-full relative cursor-pointer progress-container"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ delay: 0.4 }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const newProgress = (clickX / rect.width) * 100;
                const newTime = (newProgress / 100) * displayDuration;
                setElapsed(newTime);

                if (contentId !== 'default') {
                  NativeAudio.play({ assetId: contentId, time: newTime }).catch(() => {});
                }
              }}
            >
              <motion.div
                className="h-full rounded-full absolute top-0 left-0"
                style={{
                  background: `linear-gradient(to right, #ffffff, ${theme.gradientFrom})`,
                  width: `${progress}%`
                }}
              />
              <div
                className="absolute top-1/2 transform -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-lg cursor-pointer hover:scale-110 transition-transform touch-none"
                style={{ left: `calc(${progress}% - 10px)` }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  isSeekingRef.current = true;

                  const progressBar = document.querySelector('.progress-container') as HTMLElement;
                  if (!progressBar) return;

                  let lastClientX: number | null = null;

                  const handleMove = (e: MouseEvent | TouchEvent) => {
                    const rect = progressBar.getBoundingClientRect();
                    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
                    lastClientX = clientX;
                    const clickX = clientX - rect.left;
                    const newProgress = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
                    setElapsed((newProgress / 100) * displayDuration);
                  };

                  const handleEnd = () => {
                    isSeekingRef.current = false;
                    document.removeEventListener('mousemove', handleMove);
                    document.removeEventListener('mouseup', handleEnd);
                    document.removeEventListener('touchmove', handleMove);
                    document.removeEventListener('touchend', handleEnd);
                    if (contentId !== 'default' && lastClientX !== null) {
                      const rect = progressBar.getBoundingClientRect();
                      const newProgress = Math.max(0, Math.min(100, (lastClientX - rect.left) / rect.width * 100));
                      const newTime = (newProgress / 100) * displayDuration;
                      setElapsed(newTime);
                      // Reset timer start time when seeking, so timer counts from new position
                      if (timerDurationRef.current !== null) {
                        timerStartTimeRef.current = newTime;
                      }
                      NativeAudio.play({ assetId: contentId, time: newTime }).catch(() => {});
                    }
                  };

                  document.addEventListener('mousemove', handleMove);
                  document.addEventListener('mouseup', handleEnd);
                  document.addEventListener('touchmove', handleMove);
                  document.addEventListener('touchend', handleEnd);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  isSeekingRef.current = true;

                  const progressBar = document.querySelector('.progress-container') as HTMLElement;
                  if (!progressBar) return;

                  let lastClientX: number | null = null;

                  const handleMove = (e: TouchEvent) => {
                    e.preventDefault();
                    const rect = progressBar.getBoundingClientRect();
                    lastClientX = e.touches[0].clientX;
                    const clickX = lastClientX - rect.left;
                    const newProgress = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
                    setElapsed((newProgress / 100) * displayDuration);
                  };

                  const handleEnd = () => {
                    isSeekingRef.current = false;
                    document.removeEventListener('touchmove', handleMove);
                    document.removeEventListener('touchend', handleEnd);
                    if (lastClientX !== null && contentId !== 'default') {
                      const rect = progressBar.getBoundingClientRect();
                      const newProgress = Math.max(0, Math.min(100, (lastClientX - rect.left) / rect.width * 100));
                      const newTime = (newProgress / 100) * displayDuration;
                      setElapsed(newTime);
                      // Reset timer start time when seeking, so timer counts from new position
                      if (timerDurationRef.current !== null) {
                        timerStartTimeRef.current = newTime;
                      }
                      NativeAudio.play({ assetId: contentId, time: newTime }).catch(() => {});
                    }
                  };

                  document.addEventListener('touchmove', handleMove, { passive: false });
                  document.addEventListener('touchend', handleEnd);
                }}
              />
            </motion.div>
          </div>

          <motion.button
            onClick={handlePlayPause}
            whileTap={{ scale: 0.95 }}
            animate={{ scale: isPlaying ? [1, 0.98, 1.02, 1] : [1, 1.05, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center shadow-2xl mb-8"
          >
            {isPlaying ? <Pause className="w-10 h-10" fill="white" /> : <Play className="w-10 h-10 ml-1" fill="white" />}
          </motion.button>

          {/* Controls */}
          <div className="w-full max-w-sm relative z-50">
            <div className="flex items-center justify-between gap-4 mb-4">
              <button
                onClick={toggleMute}
                className="p-2 -ml-2 rounded-full hover:bg-white/10 active:scale-95 transition-all outline-none focus:outline-none"
              >
                {isMuted || volume === 0 ? <VolumeX className="w-5 h-5 opacity-70" /> : <Volume2 className="w-5 h-5 opacity-70" />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={e => {
                  setVolume(Number(e.target.value));
                  if (Number(e.target.value) > 0 && isMuted) setIsMuted(false);
                }}
                className="w-full h-2 bg-white/20 rounded-full appearance-none slider cursor-pointer"
                style={{ background: `linear-gradient(to right, white ${volume}%, rgba(255,255,255,0.2) ${volume}%)` }}
              />
              <span className="text-sm w-8 text-center opacity-70">{volume}</span>
            </div>
            <div className="flex justify-around items-center">
              <button
                onClick={() => setIsLooping(!isLooping)}
                className={`flex flex-col items-center gap-1.5 transition-opacity ${isLooping ? 'opacity-100' : 'opacity-60 hover:opacity-90'}`}
              >
                <RefreshCw className={`w-5 h-5 ${isLooping ? 'animate-spin-slow' : ''}`} style={{ color: isLooping ? theme.gradientFrom : 'white' }} />
                <span className="text-xs">{t('audioPlayer.loop')}</span>
              </button>
              <div 
                className="relative group"
                onMouseEnter={() => setShowTimerSelector(true)}
                onMouseLeave={() => setShowTimerSelector(false)}
              >
                <button className={`flex flex-col items-center gap-1.5 transition-opacity ${timerDuration ? 'opacity-100' : 'opacity-60 hover:opacity-90'}`}>
                  <Timer className="w-5 h-5" style={{ color: timerDuration ? theme.gradientFrom : 'white' }} />
                  <span className="text-xs">{formatTimerDuration(timerDuration)}</span>
                </button>
                <div className={`absolute bottom-full mb-2 left-1/2 -translate-x-1/2 ${showTimerSelector ? 'flex' : 'hidden'} bg-black/50 backdrop-blur-md p-2 rounded-lg gap-1`}>
                  {[10, 20, 30, 60].map(min =>
                    <button key={min} onClick={() => handleTimerSet(min)} className="px-2 py-1 text-xs rounded-md hover:bg-white/20">{min}m</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Back Button */}
        <div className="flex justify-center mt-8 mb-[50vw]">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(createPageUrl("ContentLibrary"))}
            className="flex items-center gap-2 px-6 py-3 bg-black/20 rounded-full backdrop-blur-sm hover:bg-black/30 transition-colors"
          >
            <ChevronLeft className="w-20 h-10" />
            <span className="text-sm font-medium">{t('common.back')}</span>
          </motion.button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .slider::-webkit-slider-thumb { 
            -webkit-appearance: none; 
            appearance: none; 
            width: 14px; 
            height: 14px; 
            border-radius: 50%; 
            background: white; 
            cursor: pointer; 
          }
          .slider::-moz-range-thumb { 
            width: 14px; 
            height: 14px; 
            border-radius: 50%; 
            background: white; 
            cursor: pointer; 
            border: none; 
          }
          .animate-spin-slow { 
            animation: spin 5s linear infinite; 
          }
          @keyframes spin { 
            from { transform: rotate(0deg); } 
            to { transform: rotate(360deg); } 
          }
        `
      }} />
    </div>
  );
};

export default AudioPlayer;
